import Column from 'components/Layout/Column';
import { Control, Controller } from 'react-hook-form';
import { CreateNoticeSchemaFormValues } from '../schema';
import FormField from 'further-ui/components/FormField';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { styled, TextField, Typography } from '@mui/material';
import DatePicker from 'further-ui/components/DatePicker';
import { useState } from 'react';
import api from 'lib/trpcClient';
import BasicSelect from 'further-ui/components/BasicSelect';

const OuterColumn = styled(Column)(() => ({
  maxWidth: '625px',
}));

const InnerColumn = styled(Column)(() => ({
  maxWidth: '400px',
}));

type Props = {
  readOnly?: boolean;
  currencyLcy: CurrencyCode;
  control: Control<CreateNoticeSchemaFormValues>;
  vehicleId: string;
};

const GeneralInformation: React.FC<Props> = ({
  readOnly,
  currencyLcy,
  control,
  vehicleId,
}) => {
  const [editedFields, setEditedFields] = useState({
    capitalActionName: false,
    dueDate: false,
  });

  const { data: bankAccounts } = api.bankAccount.listBankAccounts.useQuery({
    vehicleId,
  });

  const toggleFieldEdited = (field: 'capitalActionName' | 'dueDate') => {
    setEditedFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <OuterColumn spacing="lg">
      <Column spacing="xs" fullWidth>
        <Typography variant="h5">General information</Typography>
        {readOnly ? null : (
          <Typography variant="body2">
            Complete the capital notice's core information. The capital action
            name and due date are taken from the capital action builder and can
            be updated here if needed.
          </Typography>
        )}
      </Column>
      <InnerColumn spacing="lg">
        <FormField
          labelColorLight
          editable={!readOnly && !editedFields.capitalActionName}
          onEdit={() => toggleFieldEdited('capitalActionName')}
          label="Capital action name"
        >
          <Controller
            control={control}
            name="generalInformation.capitalActionName"
            render={({ field, fieldState }) =>
              editedFields.capitalActionName ? (
                <TextField
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  fullWidth
                  size="small"
                  value={field.value}
                  onChange={field.onChange}
                />
              ) : (
                <Typography variant="h6">{field.value}</Typography>
              )
            }
          />
        </FormField>
        <FormField
          labelColorLight={readOnly}
          tooltip={
            readOnly
              ? undefined
              : 'Select the notice issue date. The date can’t be after the due date below.'
          }
          label="Notice date"
        >
          <Controller
            name="generalInformation.noticeDate"
            control={control}
            render={({ field, fieldState }) =>
              readOnly ? (
                <Typography variant="h6">
                  {field.value?.toReadable()}
                </Typography>
              ) : (
                <DatePicker
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  fullWidth
                  placeholder="Select date"
                  onChange={field.onChange}
                  value={field.value}
                />
              )
            }
          />
        </FormField>
        <FormField
          labelColorLight
          editable={!readOnly && !editedFields.dueDate}
          onEdit={() => toggleFieldEdited('dueDate')}
          label="Due date"
        >
          <Controller
            control={control}
            name="generalInformation.capitalActionDueDate"
            render={({ field, fieldState }) =>
              editedFields.dueDate ? (
                <DatePicker
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  fullWidth
                  placeholder="Select date"
                  onChange={field.onChange}
                  value={field.value}
                />
              ) : (
                <Typography variant="h6">
                  {field.value?.toReadable()}
                </Typography>
              )
            }
          />
        </FormField>
        <FormField
          labelColorLight={readOnly}
          tooltip={
            readOnly
              ? undefined
              : 'Enter the contact email for any questions about the notice. If you have more than one email, please enter the email as you would like to appear after the words For any questions about this notice, please contact us via email at. For example “a@a.com, b@b.com or contact our third party administrator on c@c.com”.'
          }
          label="Main contact email(s)"
        >
          <Controller
            name="generalInformation.mainContactEmails"
            control={control}
            render={({ field, fieldState }) =>
              readOnly ? (
                <Typography variant="h6">{field.value}</Typography>
              ) : (
                <TextField
                  multiline
                  rows={6}
                  fullWidth
                  placeholder="Enter email(s)"
                  size="small"
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )
            }
          />
        </FormField>
        <FormField
          labelColorLight={readOnly}
          tooltip={
            readOnly
              ? undefined
              : 'Select the main account for receiving called funds. This may be stated in your LPA and, if so, must match that account.'
          }
          label={`${currencyLcy} recipient account`}
        >
          <Controller
            name="generalInformation.lcyRecipientAccountId"
            control={control}
            render={({ field, fieldState }) => {
              return readOnly ? (
                <Typography variant="h6">
                  {
                    bankAccounts?.find((account) => account.id === field.value)
                      ?.accountName
                  }
                </Typography>
              ) : (
                <BasicSelect
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  placeholder="Select account"
                  fullWidth
                  value={field.value}
                  onChange={field.onChange}
                  options={
                    bankAccounts
                      ?.filter(
                        (account) =>
                          !account.isDeactivated || account.id === field.value,
                      )
                      .map((account) => ({
                        value: account.id,
                        label: account.accountName,
                      })) ?? []
                  }
                />
              );
            }}
          />
        </FormField>
      </InnerColumn>
    </OuterColumn>
  );
};

export default GeneralInformation;
